import {DEFAULT_LOCALE, LocaleStatus} from "./locale-data.ts";

/**
 * Return the canonical subdomain for the given languageTagFromUrl and
 * kaLocaleOfLanguageTagFromUrl.
 *
 * NOTE: The kaLocaleOfLanguageTagFromUrl must match the languageTagFromUrl.
 * The following call would be invalid:
 * ```
 * requestCanonicalSubdomain("es-ES", "pt");
 * ```
 *
 * This function will return the canonical kaLocale for subdomains for
 * any lite or better locale.
 *
 * Usually the canonical subdomain is the same as the kaLocale,
 * but there are exceptions: for instance, for the ka-Locale
 * "zh-hans" we use zh.khanacademy.org as the canonical subdomain.
 * (This is because our partners in China are already advertising
 * the zh.khanacademy.org URL.)
 *
 * With the default language "en", we return "en".
 *
 * For everything else "www" is the canonical subdomain as we don't yet
 * have a site for them.
 */
export const requestCanonicalSubdomain = (
    langaugeTagFromUrl?: string | null,
    kaLocaleOfLanguageTagFromUrl?: string | null,
    status?: (typeof LocaleStatus)[keyof typeof LocaleStatus] | null,
): string => {
    // sync-start:request-canonical-subdomain 2044534264 https://github.com/Khan/webapp/blob/master/services/emails/url/canonical_url.go
    // sync-start:request-canonical-subdomain 2044534264 https://github.com/Khan/webapp/blob/master/pkg/services_shared/intl/urls.go
    // sync-start:request-canonical-subdomain 2044534264 https://github.com/Khan/webapp/blob/master/services/content-editing/translation_pipeline/sitemap/sitemap_index.go
    // If it is the default language 'en' then that is already the canonical.
    if (langaugeTagFromUrl && langaugeTagFromUrl === DEFAULT_LOCALE) {
        return langaugeTagFromUrl;
    }

    // If language tag is not the default locale, but the kaLocale is then
    // we want to redirect to 'www'.
    if (kaLocaleOfLanguageTagFromUrl === DEFAULT_LOCALE) {
        return "www";
    }

    // zh is the preferred subdomain for zh-hans, even though zh-hans
    // is still the official name of the locale everywhere else.
    if (kaLocaleOfLanguageTagFromUrl === "zh-hans") {
        return "zh";
    }

    if (
        kaLocaleOfLanguageTagFromUrl &&
        status &&
        status !== LocaleStatus.BEGINNING
    ) {
        return kaLocaleOfLanguageTagFromUrl;
    }

    // For all other languageTags we want www to be the canonical subdomain.
    return "www";
    // sync-end:request-canonical-subdomain
};
